import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { useAuth } from '@/hooks/useAuth';
import { useTestimonials, submitTestimonial } from '@/hooks/useTestimonials';
import { avatarOr } from '@/lib/defaultImages';

const MAX_MESSAGE = 500;

export default function TestimonialsPage() {
  const { user } = useAuth();
  const { items, loading, reload } = useTestimonials(24);

  const [name, setName] = useState(user?.name || '');
  const [role, setRole] = useState('');
  const [businessName, setBusinessName] = useState(user?.nomcommerce || '');
  const [message, setMessage] = useState('');
  const [rating, setRating] = useState(5);
  const [honeypot, setHoneypot] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const remaining = MAX_MESSAGE - message.length;

  const ratingStars = useMemo(() => [1, 2, 3, 4, 5], []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('idle');
    setErrorMsg('');

    // Anti-spam : champ piège. S'il est rempli, on simule un succès.
    if (honeypot.trim()) {
      setStatus('success');
      setMessage('');
      return;
    }
    if (!name.trim() || !message.trim()) {
      setStatus('error');
      setErrorMsg('Merci de renseigner votre nom et votre témoignage.');
      return;
    }
    if (message.length > MAX_MESSAGE) {
      setStatus('error');
      setErrorMsg(`Le témoignage ne peut pas dépasser ${MAX_MESSAGE} caractères.`);
      return;
    }

    setSubmitting(true);
    const ok = await submitTestimonial({
      userId: user?.id ?? null,
      name,
      role,
      businessName,
      message,
      rating,
      avatar: user?.image || '',
    });
    setSubmitting(false);

    if (ok) {
      setStatus('success');
      setMessage('');
      setRole('');
      reload();
    } else {
      setStatus('error');
      setErrorMsg("Une erreur est survenue. Veuillez réessayer dans un instant.");
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar forceScrolled />

      <main className="pt-24 md:pt-32 pb-16 md:pb-24">
        <div className="w-full px-4 md:px-6 lg:px-10 max-w-5xl mx-auto">
          <header className="text-center mb-10 md:mb-14">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-4">
              <i className="ri-chat-quote-line"></i>
              Témoignages
            </span>
            <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mb-3">
              Ils ont choisi ZIFEK
            </h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
              Partagez votre expérience et inspirez d&apos;autres entrepreneurs. Votre témoignage est
              publié avec les autres retours de la communauté.
            </p>
          </header>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
            {/* Formulaire */}
            <section className="rounded-xl border border-background-200/70 bg-background-50 p-5 md:p-7 h-fit">
              <h2 className="text-lg font-bold font-heading text-foreground-950 mb-1 flex items-center gap-2">
                <i className="ri-edit-2-line text-primary-600"></i>
                Laissez votre témoignage
              </h2>
              <p className="text-xs text-foreground-500 mb-5">
                Quelques lignes suffisent. Merci de rester bienveillant et sincère.
              </p>

              <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  className="absolute w-px h-px -left-[9999px] opacity-0"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-foreground-600 mb-1.5">Votre nom *</label>
                    <input
                      type="text"
                      name="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Ex : Fatima B."
                      className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-600 mb-1.5">Votre activité</label>
                    <input
                      type="text"
                      name="role"
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                      placeholder="Ex : Fondatrice, boutique beauté"
                      className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1.5">Nom de votre commerce</label>
                  <input
                    type="text"
                    name="business_name"
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    placeholder="Ex : BeautyByFatima"
                    className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1.5">Votre note</label>
                  <div className="flex items-center gap-1.5">
                    {ratingStars.map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setRating(star)}
                        className="text-2xl cursor-pointer transition-colors"
                        aria-label={`${star} étoile${star > 1 ? 's' : ''}`}
                      >
                        <i className={star <= rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}></i>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1.5">Votre témoignage *</label>
                  <textarea
                    name="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE))}
                    rows={5}
                    maxLength={MAX_MESSAGE}
                    placeholder="Racontez comment ZIFEK vous a aidé…"
                    className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 resize-none"
                  ></textarea>
                  <div className={`text-right text-[11px] mt-1 ${remaining < 40 ? 'text-accent-600' : 'text-foreground-400'}`}>
                    {message.length}/{MAX_MESSAGE}
                  </div>
                </div>

                {status === 'success' && (
                  <div className="flex items-start gap-2 px-3.5 py-2.5 rounded-lg bg-accent-50 border border-accent-200 text-accent-800 text-sm">
                    <i className="ri-checkbox-circle-line mt-0.5"></i>
                    <span>Merci ! Votre témoignage a bien été envoyé. Il sera publié après validation.</span>
                  </div>
                )}
                {status === 'error' && (
                  <div className="flex items-start gap-2 px-3.5 py-2.5 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                    <i className="ri-error-warning-line mt-0.5"></i>
                    <span>{errorMsg}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600 disabled:opacity-60 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {submitting ? (
                    <><i className="ri-loader-4-line animate-spin"></i>Envoi…</>
                  ) : (
                    <><i className="ri-send-plane-fill"></i>Publier mon témoignage</>
                  )}
                </button>

                {!user && (
                  <p className="text-[11px] text-foreground-400 text-center">
                    Vous avez un compte marchand ?{' '}
                    <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium no-underline">Connectez-vous</Link>{' '}
                    pour pré-remplir vos informations.
                  </p>
                )}
              </form>
            </section>

            {/* Liste des témoignages */}
            <section>
              <h2 className="text-lg font-bold font-heading text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-chat-quote-line text-accent-500"></i>
                Ce qu&apos;ils en disent
                {items.length > 0 && (
                  <span className="text-xs font-medium text-foreground-400">({items.length})</span>
                )}
              </h2>

              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <i className="ri-loader-4-line animate-spin text-2xl text-foreground-300"></i>
                </div>
              ) : items.length === 0 ? (
                <div className="rounded-xl border border-dashed border-background-200/70 bg-background-100/50 p-8 text-center">
                  <i className="ri-chat-quote-line text-3xl text-foreground-300"></i>
                  <p className="text-sm text-foreground-500 mt-2">
                    Aucun témoignage pour l&apos;instant. Soyez le premier à partager votre expérience !
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {items.map((item) => (
                    <article
                      key={item.id}
                      className="rounded-xl border border-background-200/70 bg-background-50 p-5"
                    >
                      <div className="flex items-center gap-1 mb-2">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <i
                            key={s}
                            className={s <= item.rating ? 'ri-star-fill text-accent-500 text-sm' : 'ri-star-line text-foreground-300 text-sm'}
                          ></i>
                        ))}
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed mb-4">{item.message}</p>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                          <img src={avatarOr(item.avatar)} alt={item.name} className="w-full h-full object-cover" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-foreground-950 truncate">{item.name}</div>
                          {(item.role || item.businessName) && (
                            <div className="text-xs text-foreground-500 truncate">
                              {[item.role, item.businessName].filter(Boolean).join(' · ')}
                            </div>
                          )}
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}